<template>
  <div id="res-topology-wrapper">
    <svg id="tree" width="100%" :height="topoHeight" />
    <a-spin v-if="loading" size="large" style="position: absolute;left:50%;top:33%" />
    <data-empty style="position: absolute;left:calc(50% - 25px);top:calc(33% - 70px)" v-if="!loading && !topoData" />
  </div>
</template>

<script>
import * as d3 from 'd3'
import * as R from 'ramda'
import { sizestr, arrayToObj } from '@/utils/utils'
import { HYPERVISORS } from '@/constants'
import { getProductName } from '@/utils/auth'
import setting from '@/config/setting'
import regionImg from '../../images/res-region.svg'
// import vpcImg from '../../images/res-vpc.svg'
// import wireImg from '../../images/res-wire.svg'
import zoneImg from '../../images/res-zone.svg'
// import storageImg from '../../images/res-bucket.svg'
// import netImg from '../../images/res-network.svg'
import cloudImg from '../../images/cloud.svg'
import accountImg from '../../images/res-cloudaccount.svg'
import hostImg from '../../images/res-host.svg'
import serverImg from '../../images/res-vminstance.svg'
import gpuImg from '../../images/gpu.svg'
import nodeImg from '../../images/node.svg'

const HYPERVISORS_MAP = arrayToObj(HYPERVISORS, 'provider')

export default {
  name: 'TopoGraph',
  components: {
    // Toolbar,
  },
  props: {
    loading: Boolean,
    dataSource: {
      type: Object,
      default: () => ({}),
    },
    domainId: String,
  },
  data () {
    return {
      topoWidth: 800,
      topoHeight: 600,
      initServerLen: 10,
      initServerHeight: 80,
      initServerWidth: 100,
      showHostId: '',
      showAccountId: '',
      showHostDepth: 5,
    }
  },
  computed: {
    brandName () {
      const { companyInfo = {} } = this.$store.state.app
      const { name, name_en } = companyInfo
      if (name || name_en) {
        return setting.language === 'en' ? name_en : name
      }
      return getProductName()
    },
    topoData () {
      const { accounts = [] } = this.dataSource
      const root = {
        name: this.brandName,
        icon: cloudImg,
        id: '1',
        type: 'brand',
        iconWidth: 90,
        iconHeight: 90,
        cpu_count: 0,
        mem_size: 0,
        vcpu_count: 0,
        vmem_size: 0,
        server_count: 0,
        host_count: 0,
        storage_size: 0,
        storage_size_mb: 0,
        storage_actual_size: 0,
        storage_actual_size_mb: 0,
        showStorageVirtualSize: false,
        storage_virtual_size: 0,
        storage_virtual_size_mb: 0,
        gpu_count: 0,
        gpu_used_count: 0,
        children: [],
      }
      accounts.forEach((account, accountIdx) => {
        const acc = {
          ...account,
          icon: accountImg,
          type: 'account',
          iconWidth: 70,
          iconHeight: 70,
          cpu_count: 0,
          mem_size: 0,
          vcpu_count: 0,
          vmem_size: 0,
          server_count: 0,
          host_count: 0,
          storage_size: 0,
          storage_actual_size: 0,
          showStorageVirtualSize: false,
          storage_virtual_size: 0,
          storage_size_mb: (account.storage_size_gb || 0) * 1024,
          storage_actual_size_mb: (account.storage_size_used_gb || 0) * 1024,
          storage_virtual_size_mb: (account.storage_size_gb || 0) * 1024 * (account.storage_cmt_bound || 0),
          gpu_count: 0,
          gpu_used_count: 0,
          name: account.name,
          children: [],
        }
        const { regions = [] } = account
        regions.map((region, regionIdx) => {
          const reg = {
            ...region,
            icon: regionImg,
            type: 'region',
            iconWidth: 60,
            iconHeight: 60,
            cpu_count: 0,
            mem_size: 0,
            vcpu_count: 0,
            vmem_size: 0,
            server_count: 0,
            host_count: 0,
            storage_size: 0,
            storage_actual_size: 0,
            showStorageVirtualSize: false,
            storage_virtual_size: 0,
            gpu_count: 0,
            gpu_used_count: 0,
            children: [],
          }
          const { zones = [] } = region
          zones.forEach((zone, zoneIdx) => {
            const zo = {
              ...zone,
              icon: zoneImg,
              type: 'zone',
              iconWidth: 50,
              iconHeight: 50,
              cpu_count: 0,
              mem_size: 0,
              vcpu_count: 0,
              vmem_size: 0,
              server_count: 0,
              host_count: 0,
              storage_size: 0,
              storage_actual_size: 0,
              showStorageVirtualSize: false,
              storage_virtual_size: 0,
              gpu_count: 0,
              gpu_used_count: 0,
              children: [],
            }
            const { hosts = [], storages = [] } = zone
            hosts.forEach((host, hostIdx) => {
              const ho = {
                ...host,
                icon: hostImg,
                type: 'host',
                iconWidth: 45,
                iconHeight: 45,
                children: [],
                vcpu_count: 0,
                vmem_size: 0,
              }
              delete ho.servers
              delete ho.isolate_devices
              const { servers = [], isolate_devices = [] } = host
              ho.server_count = servers.length
              ho.gpu_count = isolate_devices.length
              ho.gpu_used_count = isolate_devices.filter(item => item.guest_id).length
              const serverList = servers.map((server, serverIdx) => {
                ho.vcpu_count += (server.vcpu_count || 0)
                ho.vmem_size += (server.vmem_size || 0)
                return {
                  ...server,
                  icon: serverImg,
                  type: 'server',
                }
              })
              const gpuList = isolate_devices.map((gpu, gpuIdx) => {
                return {
                  ...gpu,
                  icon: gpuImg,
                  type: 'gpu',
                }
              })
              if (serverList.length) {
                ho.children.push({
                  name: 'serverList',
                  type: 'server',
                  id: `vm-${accountIdx}-${regionIdx}-${zoneIdx}-${hostIdx}-1`,
                  icon: serverImg,
                  list: [...serverList],
                  otherList: [...gpuList],
                })
              }
              if (gpuList.length) {
                ho.children.push({
                  name: 'gpuList',
                  type: 'isolate_device',
                  id: `gpu-${accountIdx}-${regionIdx}-${zoneIdx}-${hostIdx}-2`,
                  icon: gpuImg,
                  otherList: [...serverList],
                  list: [...gpuList],
                })
              }
              zo.cpu_count += ho.cpu_count
              zo.mem_size += ho.mem_size
              zo.vcpu_count += ho.vcpu_count
              zo.vmem_size += ho.vmem_size
              zo.server_count += ho.server_count
              zo.gpu_count += ho.gpu_count
              zo.gpu_used_count += ho.gpu_used_count
              zo.children.push(ho)
            })
            storages.forEach((storage, storageIdx) => {
              // const sto = {
              //   name: storage.name,
              //   id: storage.id,
              //   icon: storageImg,
              //   type: 'storage',
              //   children: [],
              // }
              zo.storage_size += storage.capacity || 0
              zo.storage_actual_size += storage.actual_capacity_used || 0
              if (storage.hasOwnProperty('cmtbound')) {
                zo.showStorageVirtualSize = true
                zo.storage_virtual_size += storage.capacity * (storage.cmtbound || 1)
              }
              // zo.children.push(sto)
            })
            reg.cpu_count += zo.cpu_count
            reg.mem_size += zo.mem_size
            reg.vcpu_count += zo.vcpu_count
            reg.vmem_size += zo.vmem_size
            reg.server_count += zo.server_count
            reg.gpu_count += zo.gpu_count
            reg.gpu_used_count += zo.gpu_used_count
            reg.host_count += hosts.length
            reg.storage_size += zo.storage_size
            reg.storage_actual_size += zo.storage_actual_size
            reg.storage_virtual_size += zo.storage_virtual_size
            if (zo.showStorageVirtualSize) {
              reg.showStorageVirtualSize = true
            }
            reg.children.push(zo)
          })
          // vpcs.forEach((vpc, vpcIdx) => {
          //   const vp = {
          //     ...vpc,
          //     icon: vpcImg,
          //     type: 'vpc',
          //     iconWidth: 50,
          //     iconHeight: 50,
          //     children: [],
          //   }
          //   const { wires = [] } = vpc
          //   wires.forEach((wire, wireIdx) => {
          //     const wi = {
          //       ...wire,
          //       icon: wireImg,
          //       type: 'wire',
          //       iconWidth: 45,
          //       iconHeight: 45,
          //       children: [],
          //     }
          //     const { networks = [] } = wire
          //     networks.forEach((network, networkIdx) => {
          //       const net = {
          //         ...network,
          //         icon: netImg,
          //         type: 'network',
          //         iconWidth: 40,
          //         iconHeight: 40,
          //         children: [],
          //       }
          //       wi.children.push(net)
          //     })
          //     vp.children.push(wi)
          //   })
          //   reg.children.push(vp)
          // })
          acc.cpu_count += reg.cpu_count
          acc.mem_size += reg.mem_size
          acc.vcpu_count += reg.vcpu_count
          acc.vmem_size += reg.vmem_size
          acc.server_count += reg.server_count
          acc.host_count += reg.host_count
          acc.gpu_count += reg.gpu_count
          acc.gpu_used_count += reg.gpu_used_count
          acc.storage_size += reg.storage_size
          acc.storage_actual_size += reg.storage_actual_size
          acc.storage_virtual_size += reg.storage_virtual_size
          if (reg.showStorageVirtualSize || acc.storage_virtual_size_mb > 0) {
            acc.showStorageVirtualSize = true
          }
          acc.children.push(reg)
        })
        root.cpu_count += acc.cpu_count
        root.mem_size += acc.mem_size
        root.vcpu_count += acc.vcpu_count
        root.vmem_size += acc.vmem_size
        root.server_count += acc.server_count
        root.host_count += acc.host_count
        root.storage_size += acc.storage_size
        root.storage_size_mb += (acc.storage_size_mb || acc.storage_size)
        root.storage_virtual_size_mb += (acc.storage_virtual_size_mb || acc.storage_virtual_size)
        root.storage_actual_size += acc.storage_actual_size
        root.storage_actual_size_mb += (acc.storage_actual_size_mb || acc.storage_actual_size)
        root.storage_virtual_size += acc.storage_virtual_size
        if (acc.showStorageVirtualSize || acc.storage_virtual_size_mb > 0) {
          root.showStorageVirtualSize = true
        }
        root.gpu_count += acc.gpu_count
        root.gpu_used_count += acc.gpu_used_count
        root.children.push(acc)
      })
      return accounts.length ? root : null
    },
    topoSimData () {
      if (this.topoData) {
        const tree = R.clone(this.topoData)
        this.simChildList(tree)
        return tree
      }
      return null
    },
    resourceType () {
      return {
        root: this.$t('common.brand'),
        brand: this.$t('common.brand'),
        account: this.$t('dictionary.cloudaccount'),
      }
    },
  },
  watch: {
    topoData: {
      handler: function (data) {
        if (data) {
          this.drawChart()
        }
      },
      deep: true,
      immediate: true,
    },
    domainId: {
      handler: function (data) {
        const svg = d3.select('#tree')
        if (svg) {
          svg.selectAll('*').remove()
        }
      },
    },
  },
  mounted () {
    const that = this
    window.addEventListener('resize', function (event) {
      that.getSize()
    })
    this.getSize()
    // this.drawChart()
  },
  methods: {
    simChildList (tree) {
      if (tree.type === 'brand') {
        tree.children.forEach(item => {
          this.simChildList(item)
        })
      } else if (tree.type === 'account') {
        const isRegion = tree.children.length && tree.children[0].type === 'region'
        if (isRegion) {
          if (tree.children.length === 1) {
            tree.children = tree.children[0].children
            this.simChildList(tree)
          } else {
            tree.children.forEach(item => {
              this.simChildList(item)
            })
          }
        } else {
          const zonLen = tree.children.filter(item => item.type === 'zone').length
          if (zonLen === 1) {
            const children = []
            tree.children.forEach(item => {
              if (item.type === 'vpc') {
                children.push(item)
              } else {
                children.push(...item.children)
              }
            })
            tree.children = children
          }
        }
      } else if (tree.type === 'region') {
        const zonLen = tree.children.filter(item => item.type === 'zone')
        if (zonLen === 1) {
          const children = []
          tree.children.forEach(item => {
            if (item.type === 'vpc') {
              children.push(item)
            } else {
              children.push(...item.children)
            }
          })
          tree.children = children
        }
      }
    },
    menuClick (type) {
      const g = d3.select('#tree').select('g')
      const data = g.node().getCTM()
      g.attr('transform', `translate(${data.e}, ${data.f}) scale(${data.d + 0.1})`)
    },
    getSize () {
      const wrapper = document.getElementById('res-topology-wrapper')
      this.topoHeight = window.innerHeight - wrapper.getBoundingClientRect().top - 50
      this.topoWidth = wrapper.getBoundingClientRect().width
    },
    drawChart () {
      const that = this
      const initialDepth = 1
      const data = this.topoSimData

      const svg = d3.select('#tree')
        .attr('width', '100%')
        .attr('height', this.topoHeight)
        .call(d3.zoom().on('zoom', (event) => {
          svg.attr('transform', event.transform)
        }))
        .append('g')
        .attr('transform', `translate(${that.topoWidth / 2}, ${that.topoHeight / 3})`)

      // 防止文字模糊不清
      svg.style('shape-rendering', 'geometricPrecision')

      // 防止第一次拖动时闪位置-start
      const zoom = d3.zoom()
        .scaleExtent([0.5, 5])
        .on('zoom', (event) => {
          svg.attr('transform', event.transform)
        })
      d3.select('#tree').call(zoom)
      const initialTransform = d3.zoomIdentity.translate(that.topoWidth / 2, that.topoHeight / 3).scale(1)
      d3.select('#tree').call(zoom.transform, initialTransform)
      // 防止第一次拖动时闪位置-end

      // 动态调整节点之间距离
      const treeLayout = d3.tree().nodeSize([200, 150]).separation((a, b) => {
        let ret = 1
        if (a.data.type === 'host') {
          if (a.children && a.children.length) {
            a.children.map(node => {
              const len = node.data.list.length
              if (len / this.initServerLen > 1) {
                ret += Math.ceil(len / this.initServerLen) * 0.25
              }
            })
          }
          if (a.children && a.children.length > 1) {
            ret += 0.25
          }
        }
        if (b.data.type === 'host') {
          if (b.children && b.children.length) {
            b.children.map(node => {
              const len = node.data.list.length
              if (len / this.initServerLen > 1) {
                ret += Math.ceil(len / this.initServerLen) * 0.25
              }
            })
          }
          if (b.children && b.children.length > 1) {
            ret += 0.25
          }
        }
        return ret
      })
      const root = d3.hierarchy(data, d => d.children)

      function initializeData (node) {
        if (node.children) {
          node._children = node.children
          node.children = null
          node._children.forEach(initializeData)
        }
      }

      root.each(d => {
        d._children = d.children
        if (d.depth >= initialDepth) {
          initializeData(d)
        }
      })

      function update (source, event) {
        // 先清空原先画布，避免重复绘制
        svg.selectAll('*').remove()

        const links = root.links()
        const nodes = root.descendants().reverse()
        treeLayout(root)

        const node = svg.selectAll('.node')
          .data(nodes, d => d.data.id)
          .join('g')
          .attr('class', 'node')
          .attr('transform', d => `translate(${d.x}, ${d.y})`)
          .on('click', (event, d) => {
            if (d.data.type === 'server' || d.data.type === 'isolate_device') return
            if (d.data.type === 'host') {
              that.showHostId = that.showHostId === d.data.id ? '' : d.data.id
              root.each(n => {
                if (n.data.type === 'host') {
                  // eslint-disable-next-line
                  if (n.data.id !== that.showHostId) {
                    if (n.children) {
                      n._children = n.children
                      n.children = null
                    }
                  } else {
                    if (n._children) {
                      n.children = n._children
                      n._children = null
                    }
                  }
                }
                // if (n.data.type === 'wire') {
                //   if (n.children) {
                //     n._children = n.children
                //     n.children = null
                //   }
                // }
              })
            }
            if (d.data.type === 'account') {
              that.showAccountId = that.showAccountId === d.data.id ? '' : d.data.id
              root.each(n => {
                if (n.data.type === 'account') {
                  // eslint-disable-next-line
                  if (n.data.id !== that.showAccountId) {
                    if (n.children) {
                      n._children = n.children
                      n.children = null
                    }
                  } else {
                    if (n._children) {
                      n.children = n._children
                      n._children = null
                    }
                  }
                }
                // if (n.data.type === 'wire') {
                //   if (n.children) {
                //     n._children = n.children
                //     n.children = null
                //   }
                // }
              })
            }
            if (d.data.type !== 'host' && d.data.type !== 'account') {
            // if (d.data.type === 'wire') {
            //   root.each(n => {
            //     if (n.data.type === 'host') {
            //       if (n.children) {
            //         n._children = n.children
            //         n.children = null
            //       }
            //     }
            //   })
            // }
              if (d.children) {
                d._children = d.children
                d.children = null
              } else {
                d.children = d._children
                d._children = null
              }
            }
            d3.select('.topo-tooltip').remove()
            update(d, event)
          })
          .on('mouseover', (event, d) => {
            if (d.data.type === 'server' || d.data.type === 'isolate_device') {
              return
            }
            d3.select('.topo-tooltip').remove()
            d3.select('body').append('div')
              .attr('class', 'topo-tooltip')
              .style('left', `${event.pageX + 20}px`)
              .style('top', `${event.pageY + 20}px`)
              .html(that.getTooltipHtml(d.data))
          })
          .on('mousemove', (event) => {
            d3.select('.topo-tooltip')
              .style('left', `${event.pageX + 20}px`)
              .style('top', `${event.pageY + 20}px`)
          })
          .on('mouseout', () => {
            d3.select('.topo-tooltip').remove()
          })

        node.append('image')
          .attr('xlink:href', function (d) {
            return d.data.icon
          })
          .attr('width', function (d) {
            return d.data.iconWidth || 50
          })
          .attr('height', function (d) {
            return d.data.iconHeight || 50
          })
          .attr('x', function (d) {
            return (d.data.iconWidth || 50) * -1 / 2
          })
          .attr('y', function (d) {
            return (d.data.iconHeight || 50) * -1 / 2
          })
          .style('cursor', 'pointer')

        const foreignObject = node.append('foreignObject')
          .attr('x', function (d) {
            return (d.data.iconWidth || 50) * -1
          })
          .attr('y', function (d) {
            return (d.data.iconHeight || 50) / 2
          })
          .attr('width', function (d) {
            return (d.data.iconWidth || 50) * 2
          })
          .attr('height', 40)
          .style('overflow', 'hidden')
          .style('text-align', 'center')
          .style('word-wrap', 'break-word')
          .style('shape-rendering', 'crispEdges')
          .style('text-overflow', 'ellipsis')

        foreignObject.append('xhtml:span')
          .style('width', '7px')
          .style('height', '7px')
          .style('background-color', 'green')
          .style('border-radius', '50%')
          .style('display', 'inline-block')
          .style('margin-right', '5px')
          .style('display', function (d) {
            if (d.data.status && ['available', 'enable', 'inservice'].includes(d.data.status)) {
              return 'inline-block'
            }
            return 'none'
          })

        foreignObject.append('xhtml:span')
          .style('word-wrap', 'break-word')
          .html(d => d.data.name)

        node.each(function (d) {
          if (d.data.type === 'host' && d.data.servers && d.data.servers.length) {
            const hostNode = d3.select(this)
            d.data.servers.map((item, index) => {
              hostNode.append('image')
                .attr('xlink:href', serverImg)
                .attr('width', 45)
                .attr('height', 45)
                .attr('x', that.getPosition('server', d.data, index).left)
                .attr('y', that.getPosition('server', d.data, index).top)
            })
          }
          if (d.data.type === 'host' && d.data.isolate_devices && d.data.isolate_devices.length) {
            const hostNode = d3.select(this)
            d.data.isolate_devices.map((item, index) => {
              hostNode.append('image')
                .attr('xlink:href', gpuImg)
                .attr('width', 35)
                .attr('height', 35)
                .attr('x', that.getPosition('isolate_device', d.data, index).left)
                .attr('y', that.getPosition('isolate_device', d.data, index).top)
            })
          }
          if (d.data.type === 'server' || d.data.type === 'isolate_device') {
            const serverNode = d3.select(this)
            serverNode.selectAll('*').remove()
            serverNode.attr('width', function (d) {
              return Math.ceil(d.data.list.length / that.initServerLen) * that.initServerWidth
            })
            serverNode.attr('height', function (d) {
              return that.initServerLen * that.initServerHeight
            })
            d.data.list.map((server, index) => {
              const position = that.getPosition(d.data.type, d.data.list, index, d.data.otherList)
              serverNode.append('image')
                .attr('xlink:href', d.data.type === 'server' ? serverImg : gpuImg)
                .attr('width', 30)
                .attr('height', 30)
                .attr('x', position.left + (that.initServerWidth - 30) / 2)
                .attr('y', position.top)
                .datum(server)
                .style('cursor', 'pointer')
                .on('mouseover', (event, d) => {
                  d3.select('.topo-tooltip').remove()
                  d3.select('body').append('div')
                    .attr('class', 'topo-tooltip')
                    .style('left', `${event.pageX + 20}px`)
                    .style('top', `${event.pageY + 20}px`)
                    .html(that.getTooltipHtml(server))
                })
                .on('mousemove', (event, d) => {
                  d3.select('.topo-tooltip')
                    .style('left', `${event.pageX + 20}px`)
                    .style('top', `${event.pageY + 20}px`)
                })
                .on('mouseout', (event, d) => {
                  d3.select('.topo-tooltip').remove()
                })
                .on('click', (event, da) => {
                  d3.select('.topo-tooltip').remove()
                  that.$router.push({
                    path: d.data.type === 'server' ? '/vminstance' : '/gpu',
                    query: { id: da.id },
                  })
                })

              if (d.data.type === 'server' && server.isolate_devices && server.isolate_devices.length) {
                serverNode.append('image')
                  .attr('xlink:href', gpuImg)
                  .attr('width', 10)
                  .attr('height', 10)
                  .attr('x', position.left + (that.initServerWidth - 30) / 2 + 19)
                  .attr('y', position.top + 13)
              }

              const foreignObject = serverNode.append('foreignObject')
                .attr('x', position.left + 5)
                .attr('y', position.top + 32)
                .attr('width', that.initServerWidth - 10)
                .attr('height', 42)
                .style('display', 'flex')
                .style('text-align', 'center')
                .style('shape-rendering', 'crispEdges')

              const span = foreignObject.append('xhtml:span')
                .html(d => (server.status === 'running' || server.guest_id ? '&nbsp;&nbsp;' : '') + server.name)
                .style('font-size', '12px')
                .style('line-height', '12px')
                .style('display', '-webkit-box')
                .style('-webkit-line-clamp', '3')
                .style('-webkit-box-orient', 'vertical')
                .style('overflow', 'hidden')
                .style('word-wrap', 'break-word')
                .style('word-break', 'break-all')
                .style('position', 'relative')
                .style('line-height', '1.2em')

              if (server.status === 'running' || server.guest_id) {
                span.append('xhtml:span')
                  .style('width', '7px')
                  .style('height', '7px')
                  .style('background-color', 'green')
                  .style('border-radius', '50%')
                  .style('position', 'absolute')
                  .style('left', '0')
                  .style('top', '2px')
              }
            })
          }

          if (!d.children && d._children && d._children.length) {
            const node = d3.select(this)
            node.append('rect')
              .attr('width', 16)
              .attr('height', 16)
              .attr('x', function (d) {
                return (d.data.iconWidth || 50) / 2 - 15
              })
              .attr('y', function (d) {
                return (d.data.iconHeight || 50) / 2 - 21
              })
              .style('fill', '#fff')
              .attr('rx', 8)
              .attr('ry', 8)
            node.append('image')
              .attr('xlink:href', nodeImg)
              .attr('width', 12)
              .attr('height', 12)
              .attr('x', function (d) {
                return (d.data.iconWidth || 50) / 2 - 14
              })
              .attr('y', function (d) {
                return (d.data.iconHeight || 50) / 2 - 20
              })
              .style('background', '#fff')
              .style('pointer-events', 'none')
          }
        })

        svg.selectAll('.link')
          .data(links, d => d.target.id)
          .join('line')
          .attr('class', 'link')
          .style('stroke', '#000')
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y + 65)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y - 35)
      }

      update(root)
    },
    getPosition (type, list = [], index, otherList = []) {
      const len = this.initServerLen
      let addWidth = this.initServerWidth
      const addHeight = this.initServerHeight
      let initLeft = (addWidth / 2) * -1
      // server 从右往左排
      if (type === 'server') {
        addWidth = addWidth * -1
        if (list.length > len) {
          initLeft = Math.ceil(list.length / len) * addWidth / 2 * -1 + addWidth
        }
        if (list.length > len * 4 && otherList.length) {
          initLeft = Math.abs(addWidth)
        }
        if (otherList.length && list.length > len) {
          initLeft = Math.min(0, Math.abs(addWidth))
        }
      } else {
        // gpu 从左往右排
        if (list.length > len) {
          initLeft = Math.ceil(list.length / len) * addWidth / 2 * -1
        }
        if (list.length > len * 4 && otherList.length) {
          initLeft = addWidth * -1
        }
        if (otherList.length && list.length > len) {
          initLeft = Math.max(-1 * addWidth, initLeft)
        }
      }
      return {
        left: Math.floor(index / len) * addWidth + initLeft,
        top: addHeight * (index % len),
      }
    },
    getTooltipHtml (data) {
      const { type } = data
      const list = []
      let str = ''
      str += `<div class="topo-tooltip-title">${this.resourceType[type] || this.$t(`dictionary.${type}`)}</div>`
      str += '<div class="topo-tooltip-line"></div>'
      list.push({ label: this.$t('common.name'), value: data.name })
      if (type === 'server') {
        const { isolate_devices = [] } = data
        list.push({ label: this.$t('compute.text_268'), value: this.getStatus(data.status || data.owner_status, 'server') })
        list.push({ label: this.$t('compute.text_295'), value: data.instance_type || '-' })
        list.push({ label: this.$t('dictionary.project'), value: data.tenant || '-' })
        list.push({ label: 'IP', value: data.ip_addr || '-' })
        list.push({ label: this.$t('system.vcpu_count'), value: data.vcpu_count })
        list.push({ label: this.$t('system.vmem_size'), value: sizestr(data.vmem_size, 'M', 1024) })

        isolate_devices.forEach((item, index) => {
          list.push({ label: this.$t('compute.text_113') + ' ' + (index + 1), value: item.name })
        })
      }
      if (['host', 'zone', 'region', 'account', 'brand'].includes(type)) {
        const { vcpu_count = 0, vmem_size = 0, cpu_count = 0, mem_size = 0, server_count } = data
        list.push({ label: this.$t('system.cpu_count'), value: cpu_count })
        list.push({ label: this.$t('system.vcpu_count'), value: vcpu_count })
        list.push({ label: this.$t('system.mem_size'), value: sizestr(mem_size, 'M', 1024) })
        list.push({ label: this.$t('system.vmem_size'), value: sizestr(vmem_size, 'M', 1024) })
        list.push({ label: this.$t('cloudenv.text_72'), value: server_count })
      }
      if (type === 'host') {
        list.push({ label: this.$t('compute.passthrough_device_count'), value: data.gpu_count })
        list.push({ label: this.$t('system.gpu_used_count'), value: data.gpu_used_count })
      }
      if (type === 'account' || type === 'brand') {
        const { host_count = 0, gpu_count = 0, gpu_used_count = 0, storage_size = 0, storage_size_mb = 0, storage_actual_size = 0, storage_actual_size_mb = 0, storage_virtual_size_mb = 0, storage_virtual_size = 0, showStorageVirtualSize = false } = data
        list.push({ label: this.$t('dashboard.text_136'), value: host_count })
        list.push({ label: this.$t('compute.passthrough_device_count'), value: gpu_count })
        list.push({ label: this.$t('system.gpu_used_count'), value: gpu_used_count })
        list.push({ label: this.$t('k8s.text_312'), value: sizestr(storage_size_mb || storage_size, 'M', 1024) })
        list.push({ label: this.$t('dashboard.text_123'), value: sizestr(storage_actual_size_mb || storage_actual_size, 'M', 1024) })
        if (showStorageVirtualSize) {
          list.push({ label: this.$t('system.storage_virtual_size'), value: sizestr(storage_virtual_size_mb || storage_virtual_size, 'M', 1024) })
        }
      }
      if (type === 'network') {
        list.push({ label: this.$t('compute.text_268'), value: this.getStatus(data.status, 'network') })
        list.push({ label: this.$t('system.text_362'), value: data.guest_gateway || '-' })
        list.push({ label: this.$t('cloudenv.text_185'), value: data.guest_ip_start || '-' })
        list.push({ label: this.$t('cloudenv.text_186'), value: data.guest_ip_end || '-' })
        list.push({ label: this.$t('compute.text_583'), value: data.guest_ip_mask || '-' })
        list.push({ label: 'VLAN ID', value: data.vlan_id || '-' })
      }
      if (['vpc', 'region'].includes(type)) {
        list.push({ label: this.$t('compute.text_268'), value: this.getStatus(data.status, type) })
      }
      if (['zone'].includes(type)) {
        list.push({ label: this.$t('compute.text_268'), value: data.status === 'enable' ? this.$t('status.vpc.available') : '-' })
      }
      if (type === 'account') {
        list.push({ label: this.$t('common.brand'), value: HYPERVISORS_MAP[data.provider].label || '-' })
      }
      list.forEach((item, index) => {
        str += `<div class="topo-tooltip-item"><span class="topo-tooltip-item-label">${item.label}: </span><span>${item.value}</span></div>`
      })
      return str
    },
    getStatus (status, statusModule) {
      if (!status) return this.$t('common.text00001')
      return this.$t(`status.${statusModule}.${status}`)
    },
  },
}
</script>

<style>
#res-topology-wrapper {
  position: relative;
  cursor: grab;
}
.topo-tooltip {
  min-width: 200px;
  max-width: 300px;
  min-height: 200px;
  background: rgb(250, 252, 254);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 10px;
  position: absolute;
}
.topo-tooltip-title {
  font-weight: bold;
  font-size: 16px;
}
.topo-tooltip-line {
  width: 100%;
  border-top: solid 1px #ccc;
  margin: 5px 0;
}
.topo-tooltip-item {
  font-size: 14px;
  margin: 3px 0;
}
.topo-tooltip-item-label {
  margin-right: 5px;
}
.topo-item-status {
  width: 5px;
  height: 5px;
  background: green;
}
</style>
